/* =============================================================================
   JAPANSOLVED HOME v13.1 HERO PAGER SANCTUARY JS
   Purpose: homepage-only behavior bundle for safe parallel testing.
   Scope: .js-homepage and [data-jshs-root] only.
   Install target: assets/japansolved-home-v13-clean.js

   Preserves current homepage behavior:
   - Four-system hero controller: desktop-dark, desktop-light, mobile-dark, mobile-light
   - Hero dots, manual navigation, timed autoplay, visibility pause/resume
   - Theme/view switching via data-js-theme and data-js-view events
   - Reduced-motion support
   - Desktop-light safezone / luma attribute fallback
   - Tablet active-system prewarm
   - Missing-link reveal
   - Unified section reveal
   - Service tier explainer card interactions
   - Footer legal-gold phrase wrapping

   Safety doctrine:
   - Does not touch global CSS or non-homepage pages.
   - Uses destroy hooks to avoid duplicate controllers during Shopify preview/editor reloads.
   - Keeps selectors scoped to homepage systems.
   ============================================================================= */
(function () {
  'use strict';

  var NS = 'JapanSolvedHomeV13_1';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function on(target, type, handler, options, cleanup) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(type, handler, options || false);
    if (cleanup) {
      cleanup.push(function () {
        target.removeEventListener(type, handler, options || false);
      });
    }
  }

  function getMotionReduced() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  function idle(fn, timeout) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fn, { timeout: timeout || 1800 });
    } else {
      window.setTimeout(fn, 220);
    }
  }

  function extractBgUrl(slide) {
    var raw = slide && slide.style ? slide.style.getPropertyValue('--jshs-bg') || '' : '';
    var match = raw.match(/url\(["']?([^"')]+)["']?\)/i);
    return match ? match[1] : '';
  }

  function preloadUrl(url, eager) {
    if (!url || preloadUrl.done[url]) return;
    preloadUrl.done[url] = true;
    var img = new Image();
    img.decoding = 'async';
    if (eager) img.loading = 'eager';
    img.src = url;
    if (img.decode) img.decode().catch(function () {});
  }
  preloadUrl.done = {};

  function preloadSlide(slide, eager) {
    preloadUrl(extractBgUrl(slide), eager);
  }

  function initHeroController(cleanup) {
    var root = document.querySelector('[data-jshs-root]');
    if (!root) return;

    if (window.__jshsStableController && window.__jshsStableController.destroy) {
      try { window.__jshsStableController.destroy(); } catch (e) {}
    }

    var systems = Array.prototype.slice.call(root.querySelectorAll('[data-jshs-system]'));
    if (!systems.length) return;

    var reduce = getMotionReduced();
    var mobileMq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    var transitionMs = 920;
    var activeKey = null;
    var activeSystem = null;
    var timer = null;
    var manualTimer = null;
    var resizeTimer = null;
    var hidden = document.hidden;
    var hoverPaused = false;
    var state = {};

    var durations = {
      'desktop-dark': [3000, 2500, 21000, 19200, 16800, 20400, 21600, 17400, 22800],
      'desktop-light': [19800, 18600, 21000, 19200, 20400, 18600, 22200, 18000, 22800],
      'mobile-dark': [3000, 2500, 6000, 5600, 5200, 5800, 6200, 5400, 6600],
      'mobile-light': [5200, 4800, 5400, 5200, 5000, 5400, 5800, 5000, 6200]
    };

    function getKey() {
      var theme = document.documentElement.getAttribute('data-js-theme') === 'light' ? 'light' : 'dark';
      var forcedDesktop = document.documentElement.getAttribute('data-js-view') === 'desktop';
      var isMobile = mobileMq ? mobileMq.matches && !forcedDesktop : false;
      return (isMobile ? 'mobile' : 'desktop') + '-' + theme;
    }

    function slides(sys) {
      return sys ? Array.prototype.slice.call(sys.querySelectorAll('.jshs-slide')) : [];
    }

    function dots(sys) {
      return sys ? Array.prototype.slice.call(sys.querySelectorAll('.jshs-dot')) : [];
    }

    function durationFor(sys, index) {
      var key = sys ? sys.getAttribute('data-jshs-system') : activeKey;
      var perSlide = durations[key] || [];
      var specific = perSlide[index];
      var fromSlide = sys && slides(sys)[index] ? parseInt(slides(sys)[index].getAttribute('data-duration'), 10) : 0;
      var fromSystem = sys ? parseInt(sys.getAttribute('data-interval'), 10) : 0;
      return specific || fromSlide || fromSystem || 7600;
    }

    function buildDots(sys) {
      var sysKey = sys.getAttribute('data-jshs-system');
      var slideList = slides(sys);
      var wrap = sys.querySelector('[data-jshs-dots]');
      state[sysKey] = state[sysKey] || { index: 0 };
      if (!wrap) return;
      wrap.innerHTML = '';
      slideList.forEach(function (_, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'jshs-dot';
        dot.setAttribute('aria-label', 'Go to hero slide ' + (index + 1));
        dot.addEventListener('click', function () {
          clearManual();
          state[sysKey].index = index;
          paint(sys, index);
          manualTimer = window.setTimeout(function () {
            schedule(durationFor(sys, index));
          }, reduce ? 0 : Math.max(transitionMs, 850));
        });
        wrap.appendChild(dot);
      });
    }

    function paint(sys, index) {
      var slideList = slides(sys);
      var dotList = dots(sys);
      if (!slideList.length) return;
      var safeIndex = ((index % slideList.length) + slideList.length) % slideList.length;
      slideList.forEach(function (slide, i) {
        var active = i === safeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          var activeDuration = durationFor(sys, safeIndex);
          slide.style.setProperty('--jshs-active-duration', activeDuration + 'ms');
          preloadSlide(slide, true);
        }
      });
      dotList.forEach(function (dot, i) {
        var active = i === safeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      state[sys.getAttribute('data-jshs-system')].index = safeIndex;
      warmAround(sys, safeIndex);
    }

    function warmAround(sys, index) {
      var slideList = slides(sys);
      if (!slideList.length) return;
      preloadSlide(slideList[index], true);
      idle(function () { preloadSlide(slideList[(index + 1) % slideList.length], false); }, 1000);
      idle(function () { preloadSlide(slideList[(index + 2) % slideList.length], false); }, 1800);
    }

    function clearTimer() {
      if (timer) window.clearTimeout(timer);
      timer = null;
    }

    function clearManual() {
      if (manualTimer) window.clearTimeout(manualTimer);
      manualTimer = null;
    }

    function schedule(delay) {
      clearTimer();
      if (reduce || hidden || hoverPaused || !activeSystem) return;
      var current = state[activeKey] ? state[activeKey].index || 0 : 0;
      var wait = delay || durationFor(activeSystem, current);
      timer = window.setTimeout(function () {
        var list = slides(activeSystem);
        if (!list.length) return;
        var next = ((state[activeKey].index || 0) + 1) % list.length;
        paint(activeSystem, next);
        schedule(durationFor(activeSystem, next));
      }, wait);
    }

    function activate(force) {
      var key = getKey();
      var sys = root.querySelector('[data-jshs-system="' + key + '"]') || systems[0];
      if (!sys) return;
      if (!force && key === activeKey && sys === activeSystem) return;

      activeKey = key;
      activeSystem = sys;

      systems.forEach(function (item) {
        var onState = item === sys;
        item.classList.toggle('is-system-active', onState);
        item.setAttribute('aria-hidden', onState ? 'false' : 'true');
      });

      state[key] = state[key] || { index: 0 };
      paint(sys, state[key].index || 0);
      root.classList.add('is-ready');
      schedule(durationFor(sys, state[key].index || 0));
    }

    function pointerEnter() {
      if (window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches) {
        hoverPaused = true;
        clearTimer();
      }
    }

    function pointerLeave() {
      hoverPaused = false;
      schedule();
    }

    function visibility() {
      hidden = document.hidden;
      if (hidden) clearTimer();
      else schedule(900);
    }

    function softResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        var key = getKey();
        if (key !== activeKey) activate(true);
      }, 220);
    }

    function themeOrViewChange() { activate(true); }
    function orientationHandler() { window.setTimeout(softResize, 260); }

    systems.forEach(function (sys) {
      var sysKey = sys.getAttribute('data-jshs-system');
      state[sysKey] = state[sysKey] || { index: 0 };
      buildDots(sys);
      paint(sys, 0);
    });

    on(root, 'mouseenter', pointerEnter, false, cleanup);
    on(root, 'mouseleave', pointerLeave, false, cleanup);
    on(document, 'visibilitychange', visibility, false, cleanup);
    on(window, 'resize', softResize, { passive: true }, cleanup);
    on(window, 'orientationchange', orientationHandler, { passive: true }, cleanup);
    on(window, 'japansolved-theme-change', themeOrViewChange, false, cleanup);
    on(window, 'japansolved-view-change', themeOrViewChange, false, cleanup);

    if (mobileMq && mobileMq.addEventListener) {
      mobileMq.addEventListener('change', themeOrViewChange);
      cleanup.push(function () { mobileMq.removeEventListener('change', themeOrViewChange); });
    } else if (mobileMq && mobileMq.addListener) {
      mobileMq.addListener(themeOrViewChange);
      cleanup.push(function () { mobileMq.removeListener(themeOrViewChange); });
    }

    window.__jshsStableController = {
      destroy: function () {
        clearTimer();
        clearManual();
        cleanup.splice(0).forEach(function (fn) { try { fn(); } catch (e) {} });
      }
    };

    activate(true);
  }

  function initTabletPrewarm(cleanup) {
    var root = document.querySelector('[data-jshs-root]');
    if (!root) return;
    var tabletMq = window.matchMedia ? window.matchMedia('(min-width: 750px) and (max-width: 1180px)') : null;
    var done = {};

    function prewarmActiveTablet() {
      if (!tabletMq || !tabletMq.matches) return;
      var sys = root.querySelector('.jshs-system.is-system-active');
      if (!sys) return;
      var key = sys.getAttribute('data-jshs-system') || 'active';
      if (done[key]) return;
      done[key] = true;
      Array.prototype.forEach.call(sys.querySelectorAll('.jshs-slide'), function (slide) {
        preloadSlide(slide, true);
      });
    }

    function delayedPrewarm(ms) {
      window.setTimeout(prewarmActiveTablet, ms || 450);
    }

    on(window, 'load', function () { delayedPrewarm(250); }, false, cleanup);
    on(window, 'japansolved-theme-change', function () { delayedPrewarm(450); }, false, cleanup);
    on(window, 'japansolved-view-change', function () { delayedPrewarm(450); }, false, cleanup);
    on(window, 'resize', function () { delayedPrewarm(450); }, { passive: true }, cleanup);
    prewarmActiveTablet();
  }

  function initLightDesktopSafezone(cleanup) {
    var root = document.querySelector('[data-jshs-root]');
    if (!root) return;

    var fallback = {
      1: 'clean',
      2: 'clean',
      3: 'busy',
      4: 'pocket',
      5: 'busy-dark',
      6: 'clean',
      7: 'clean',
      8: 'dark',
      9: 'dark'
    };

    function setFallback(slide) {
      var index = Number(slide.getAttribute('data-slide'));
      var mode = fallback[index] || 'clean';
      slide.setAttribute('data-js-safezone', mode);
      if (mode === 'busy' || mode === 'busy-dark') slide.setAttribute('data-js-luma', 'busy');
      if (mode === 'dark') slide.setAttribute('data-js-luma', 'dark');
    }

    function sample(slide) {
      var url = extractBgUrl(slide);
      if (!url || slide.__jshsLumaDone) return;
      slide.__jshsLumaDone = true;

      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () {
        try {
          var canvas = document.createElement('canvas');
          canvas.width = 40;
          canvas.height = 24;
          var ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return;
          ctx.drawImage(img, 0, 0, img.width * 0.58, img.height, 0, 0, 40, 24);
          var data = ctx.getImageData(0, 0, 40, 24).data;
          var total = 0;
          var variance = 0;
          var values = [];
          for (var i = 0; i < data.length; i += 4) {
            var y = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
            values.push(y);
            total += y;
          }
          var avg = total / values.length;
          values.forEach(function (v) { variance += (v - avg) * (v - avg); });
          variance = variance / values.length;
          if (avg < 0.36) slide.setAttribute('data-js-luma', 'dark');
          else if (variance > 0.055) slide.setAttribute('data-js-luma', 'busy');
          else slide.setAttribute('data-js-luma', 'clean');
        } catch (e) {}
      };
      img.src = url;
    }

    function run() {
      var slideList = Array.prototype.slice.call(document.querySelectorAll('[data-jshs-system="desktop-light"] .jshs-slide'));
      slideList.forEach(function (slide) {
        setFallback(slide);
        sample(slide);
      });
    }

    run();
    on(window, 'japansolved-theme-change', run, false, cleanup);
    on(window, 'japansolved-view-change', run, false, cleanup);
  }

  function initMissingLinkReveal() {
    var root = document.querySelector('[data-js-missing-link-reveal]');
    if (!root) return;
    var reduceMotion = getMotionReduced();
    root.classList.add('is-ml-ready');

    function reveal() { root.classList.add('is-ml-revealed'); }

    if (reduceMotion) {
      reveal();
      return;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            reveal();
            observer.disconnect();
          }
        });
      }, { threshold: 0.16, rootMargin: '0px 0px -12% 0px' });
      observer.observe(root);
    } else {
      reveal();
    }
  }

  function initUnityReveal() {
    var root = document.querySelector('.js-homepage');
    if (!root) return;
    var reduceMotion = getMotionReduced();

    var selectors = [
      '.js-request-bridge-panel',
      '.js-layers-kicker', '.js-layers-title', '.js-layers-list span', '.js-layers-divider',
      '.js-layers-copy', '.js-layers-friction', '.js-layers-removal', '.js-layers-platform', '.js-layers-closing', '.js-layers-thesis',
      '.js-human-layer .js-section-kicker', '.js-human-layer .js-emotional-title', '.js-human-layer .js-emotional-lead',
      '.js-human-layer .js-judgment-row span', '.js-human-layer .js-emotional-copy p', '.js-human-layer .js-emotional-statement', '.js-human-layer .js-capability-row span',
      '.js-section-title', '.js-card', '.js-process-item', '.js-case-card', '.js-tier-card',
      '.js-engagement-detail-panel', '.js-engagement-detail-cta',
      '.js-final-drama-eyebrow', '.js-final-drama h2', '.js-final-drama-lead', '.js-final-drama-question', '.js-final-drama-identities li', '.js-final-drama-close',
      '.js-final-transition-divider', '.js-final-drama-chamber', '.js-final-drama-chamber-copy p', '.js-final-drama-stamp',
      '.js-final-inner > *'
    ];

    var elements = [];
    selectors.forEach(function (selector) {
      Array.prototype.forEach.call(root.querySelectorAll(selector), function (el) {
        if (elements.indexOf(el) === -1) elements.push(el);
      });
    });

    elements.forEach(function (el) {
      el.classList.add('jsh-unity-reveal');
      if (el.matches('.js-card, .js-process-item, .js-case-card, .js-tier-card, .js-layers-list span, .js-human-layer .js-judgment-row span, .js-human-layer .js-capability-row span, .js-final-drama-identities li')) {
        el.classList.add('jsh-unity-reveal-card');
      } else {
        el.classList.add('jsh-unity-reveal-soft');
      }
    });

    function assignIndexes(containerSelector, childSelector) {
      Array.prototype.forEach.call(root.querySelectorAll(containerSelector), function (container) {
        Array.prototype.forEach.call(container.querySelectorAll(childSelector), function (el, index) {
          el.style.setProperty('--jsh-reveal-index', Math.min(index, 12));
        });
      });
    }

    assignIndexes('.js-layers-list', 'span');
    assignIndexes('.js-human-layer .js-judgment-row', 'span');
    assignIndexes('.js-human-layer .js-capability-row', 'span');
    assignIndexes('.js-grid-8', '.js-card');
    assignIndexes('.js-process-row', '.js-process-item');
    assignIndexes('.js-case-grid', '.js-case-card');
    assignIndexes('.js-tier-grid', '.js-tier-card');
    assignIndexes('.js-final-drama-identities', 'li');
    assignIndexes('.js-final-drama-chamber-copy', 'p');

    if (reduceMotion) {
      elements.forEach(function (el) { el.classList.add('is-jsh-revealed'); });
      return;
    }

    document.documentElement.classList.add('jsh-unity-motion-ready');

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('is-jsh-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          entry.target.classList.add('is-jsh-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -9% 0px' });

    elements.forEach(function (el) { observer.observe(el); });
  }

  function initInteractionRefinements() {
    var tierDetails = document.getElementById('js-tier-service-explainer') || document.querySelector('.js-tier-explainer-v2');
    var tierCards = Array.prototype.slice.call(document.querySelectorAll('.js-tier-grid > .js-tier-card'));

    function setTierAria() {
      tierCards.forEach(function (card) {
        card.setAttribute('aria-expanded', tierDetails && tierDetails.open ? 'true' : 'false');
      });
    }

    function openTierExplainer() {
      if (!tierDetails) return;
      tierDetails.open = true;
      setTierAria();
      window.requestAnimationFrame(function () {
        var panel = tierDetails.querySelector('.js-tier-exp-panel') || tierDetails;
        try { panel.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        catch (e) { panel.scrollIntoView(true); }
      });
    }

    tierCards.forEach(function (card) {
      card.addEventListener('click', function (event) {
        if (event.target.closest('a,button,summary')) return;
        openTierExplainer();
      });
      card.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openTierExplainer();
        }
      });
    });

    if (tierDetails) {
      tierDetails.addEventListener('toggle', setTierAria);
      setTierAria();
    }

    wrapFooterLegalGold();
  }

  function wrapFooterLegalGold() {
    var footer = document.querySelector('footer');
    if (!footer || footer.dataset.jsLegalGoldProcessed) return;
    footer.dataset.jsLegalGoldProcessed = 'true';

    var phrases = [
      'All fees are non-refundable once received.',
      'Agreements are non-cancellable.'
    ];

    var walker = document.createTreeWalker(footer, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
        return phrases.some(function (phrase) { return node.nodeValue.indexOf(phrase) > -1; })
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });

    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach(function (node) {
      var text = node.nodeValue;
      var fragment = document.createDocumentFragment();
      var pattern = new RegExp('(' + phrases.map(function (phrase) {
        return phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }).join('|') + ')', 'g');
      var last = 0;
      var match;

      while ((match = pattern.exec(text))) {
        if (match.index > last) fragment.appendChild(document.createTextNode(text.slice(last, match.index)));
        var span = document.createElement('span');
        span.className = 'js-footer-legal-gold';
        span.textContent = match[0];
        fragment.appendChild(span);
        last = pattern.lastIndex;
      }

      if (last < text.length) fragment.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(fragment, node);
    });
  }

  ready(function () {
    var homepage = document.querySelector('.js-homepage');
    if (!homepage) return;

    if (window.__JapanSolvedHomeV13 && window.__JapanSolvedHomeV13.destroy) {
      try { window.__JapanSolvedHomeV13.destroy(); } catch (e) {}
    }

    var cleanup = [];

    initHeroController(cleanup);
    initTabletPrewarm(cleanup);
    initLightDesktopSafezone(cleanup);
    initMissingLinkReveal();
    initUnityReveal();
    initInteractionRefinements();

    window.__JapanSolvedHomeV13 = {
      version: 'v13.1-hero-pager-sanctuary',
      destroy: function () {
        if (window.__jshsStableController && window.__jshsStableController.destroy) {
          try { window.__jshsStableController.destroy(); } catch (e) {}
        }
        cleanup.splice(0).forEach(function (fn) { try { fn(); } catch (e) {} });
      }
    };

    if (window.console && window.console.info) {
      window.console.info(NS + ' loaded: v13.1-hero-pager-sanctuary');
    }
  });
})();
